import { useParams, Link, Navigate } from 'react-router-dom';
import {
  getExercise,
  relatedExercises,
  alternatesForExercise,
  getTechniqueName,
  equipmentLabel,
  exercisesBase,
} from '../lib/exercises';
import { SITE_URL, getStrings, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { CtaBlock } from '../components/CtaBlock';
import { Thumb } from '../components/Thumb';

const difficultyColor: Record<string, string> = {
  beginner: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30',
  intermediate: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30',
  advanced: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30',
};

function Badge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${className}`}>
      {children}
    </span>
  );
}

export default function ExercisePage({ lang = 'en' as Lang }: { lang?: Lang }) {
  const { slug } = useParams();
  const c = getStrings(lang).exercisesChrome;
  const base = exercisesBase(lang);
  const ex = slug ? getExercise(lang, slug) : undefined;
  if (!ex) return <Navigate to={base} replace />;

  const url = `${SITE_URL}${base}/${ex.slug}`;
  const title = `${ex.t.name} — ${c.titleSuffix} | ArmProgress`;
  const description = ex.seo.metaDescription || ex.t.description;
  const related = relatedExercises(ex, lang);
  const techniques = ex.relatedTechniqueIds.map((id) => ({ id, name: getTechniqueName(id, lang) }));

  const howToLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: ex.t.name,
    description: ex.seo.seoIntro || ex.t.description,
    step: ex.t.instructions.map((text, i) => ({ '@type': 'HowToStep', position: i + 1, text })),
    supply: ex.equipmentKeys
      .filter((k) => k !== 'none')
      .map((k) => ({ '@type': 'HowToSupply', name: equipmentLabel(k, lang) })),
  };
  const faq = ex.seo.faq || [];
  const faqLd = faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={title}
        description={description}
        canonical={url}
        type="article"
        lang={lang}
        jsonLd={faqLd ? [howToLd, faqLd] : howToLd}
        alternates={alternatesForExercise(ex.id)}
      />
      <BlogHeader lang={lang} />

      <article className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to={base} className="text-faint hover:text-ink text-sm">
          {c.backToIndex}
        </Link>

        {/* Hero */}
        <div className="mt-4 mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge className={difficultyColor[ex.difficulty]}>{c.difficulty[ex.difficulty as keyof typeof c.difficulty]}</Badge>
            <Badge className="border-line bg-surface/60 text-muted">{c.category[ex.category as keyof typeof c.category]}</Badge>
            {ex.armGroup && (
              <Badge className="border-primary/30 bg-primary/10 text-primary">
                {c.armGroup[ex.armGroup as keyof typeof c.armGroup]}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-ink leading-tight mb-3">
            {ex.t.name}
          </h1>
          {ex.seo.seoIntro && <p className="text-muted text-lg leading-relaxed">{ex.seo.seoIntro}</p>}
        </div>

        {/* Video, or a self-hosted still frame as the hero */}
        {ex.videoUrl ? (
          <div className="mb-10 rounded-2xl overflow-hidden border border-line bg-surface/60">
            <video
              src={ex.videoUrl}
              poster={ex.imageUrl}
              controls
              preload="metadata"
              playsInline
              className="w-full aspect-video bg-black"
            />
          </div>
        ) : (
          <Thumb
            src={ex.imageUrl}
            alt={ex.t.name}
            emoji={ex.emoji}
            className="mb-10 aspect-video w-full rounded-2xl border border-line"
            imgClassName="object-top"
          />
        )}

        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <div className="rounded-2xl border border-line bg-surface/60 p-5">
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-2">{c.musclesLabel}</h2>
            <p className="text-muted">{ex.t.muscles}</p>
          </div>
          <div className="rounded-2xl border border-line bg-surface/60 p-5">
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-2">{c.equipmentLabel}</h2>
            <div className="flex flex-wrap gap-2">
              {ex.equipmentKeys.map((k) => (
                <span key={k} className="text-muted text-sm bg-elevated px-2.5 py-1 rounded-full border border-line">
                  {equipmentLabel(k, lang)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-extrabold text-ink mb-4">{c.instructionsLabel}</h2>
          <ol className="space-y-3">
            {ex.t.instructions.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/15 border border-primary/30 text-primary font-bold text-sm flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-muted leading-relaxed pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <div className="rounded-2xl border border-line bg-surface/60 p-5 mb-10">
          <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-2">{c.setsRepsLabel}</h2>
          <p className="text-ink text-lg font-semibold">{ex.t.sets} · {ex.t.reps}</p>
        </div>

        {ex.t.tips.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.tipsLabel}</h2>
            <ul className="space-y-2">
              {ex.t.tips.map((tip, i) => (
                <li key={i} className="flex gap-3 text-muted leading-relaxed">
                  <span className="text-primary flex-shrink-0">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </section>
        )}

        {ex.seo.commonMistakes && ex.seo.commonMistakes.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.mistakesLabel}</h2>
            <ul className="space-y-2">
              {ex.seo.commonMistakes.map((m, i) => (
                <li key={i} className="flex gap-3 text-muted leading-relaxed">
                  <span className="text-red-600 flex-shrink-0">✕</span>
                  {m}
                </li>
              ))}
            </ul>
          </section>
        )}

        {techniques.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-ink mb-3">{c.relatedTechniquesLabel}</h2>
            <div className="flex flex-wrap gap-2">
              {techniques.map((t) => (
                <Link
                  key={t.id}
                  to={`${base}?technique=${t.id}`}
                  className="text-sm bg-elevated hover:bg-line text-muted hover:text-ink px-3 py-1.5 rounded-full border border-line transition"
                >
                  {t.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        {faq.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-extrabold text-ink mb-4">{c.faqLabel}</h2>
            <div className="space-y-5">
              {faq.map((f, i) => (
                <div key={i}>
                  <h3 className="text-ink font-semibold mb-1">{f.q}</h3>
                  <p className="text-muted">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-faint text-xs leading-relaxed mb-10 border-t border-line pt-6">{c.disclaimer}</p>

        <CtaBlock lang={lang} />

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-ink mb-4">{c.relatedExercisesLabel}</h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`${base}/${r.slug}`}
                    className="flex items-center gap-2 text-primary hover:text-gold-light rounded-lg px-3 py-2 hover:bg-surface/60 transition"
                  >
                    {r.t.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>

      <Footer lang={lang} />
    </div>
  );
}
